import { decodeSifBaseAsObject } from '@hmhco/amp-core/src/userContext/sifDecoder';

/**
 * Holds a delegated ("acting as") SIF token for the duration of a workflow where
 * the logged-in user operates on behalf of another user, e.g. a coach viewing a
 * teacher's class performance.
 *
 * The token is obtained via the RFC 8693 token exchange and carries a `may_act`
 * claim naming the subject. It is intentionally kept in memory only - it must not
 * outlive the tab or be readable from storage.
 */
let actingUser = null;

/**
 * Expiry margin: treat a token as expired slightly early so an in-flight request
 * cannot be sent with a token that lapses server-side before it arrives.
 */
const EXPIRY_SKEW_MS = 30 * 1000;

/**
 * Derive the absolute expiry, preferring the token's own `exp` claim and falling
 * back to the `expires_in` value from the exchange response.
 *
 * @param {string} token raw SIF token, including the `SIF_HMACSHA256 ` prefix
 * @param {number} [expiresIn] lifetime in seconds
 * @returns {number} epoch milliseconds, or 0 when it cannot be determined
 */
export const getTokenExpiry = (token, expiresIn) => {
  try {
    const { exp } = decodeSifBaseAsObject(token) || {};
    if (exp > 0) {
      return exp * 1000;
    }
  } catch {
    // fall through to expires_in below
  }

  return expiresIn > 0 ? Date.now() + expiresIn * 1000 : 0;
};

/**
 * Record the delegated token for a subject.
 *
 * @param {object} params
 * @param {string} params.userRefId refId of the user being acted upon
 * @param {string} params.token SIF token to send as the Authorization header
 * @param {number} params.expiresAt epoch milliseconds
 */
export const setActingUser = ({ userRefId, token, expiresAt }) => {
  actingUser = { userRefId, token, expiresAt };
};

export const clearActingUser = () => {
  actingUser = null;
};

/**
 * @returns {boolean} whether a usable, unexpired delegated token is held
 */
export const isActingAsUser = () => {
  if (!actingUser?.token) {
    return false;
  }
  const { expiresAt } = actingUser;
  if (!expiresAt) {
    return true;
  }
  return expiresAt - EXPIRY_SKEW_MS > Date.now();
};

/**
 * The delegated token, for use in place of the logged-in user's SIF token.
 * Callers should fall back to their own token when this returns null.
 *
 * @returns {string|null}
 */
export const getActingUserToken = () =>
  isActingAsUser() ? actingUser.token : null;

/**
 * The refId of the user currently being acted upon. Useful for scoping cache
 * keys so delegated responses cannot be served to the logged-in user.
 *
 * @returns {string|null}
 */
export const getActingUserRefId = () =>
  isActingAsUser() ? actingUser.userRefId : null;
